import Vue from 'vue';
import { RouteName } from '@/shared/enums';
import { updateLink } from '@/shared/utils/seo/update-link';
import { transformDeeplinkToAction, transformDeeplinkToRoute } from '@/shared/utils/transform-deeplink';

const DEEPLINK_REGEX = /^\/deeplink/;
const DEEPLINK_ACTION_REGEX = /^\/deeplink\/action\//;

export default ({ $EventBus, $router, $services, $spinner, $storage }) => {
  const subscriber = $services.resolve('subscriber');
  const liveManager = $services.resolve('liveManager');

  $router.reload = (name) => {
    const path = $router.currentRoute.fullPath;

    $router.replace({ name: RouteName.BLANK });

    Vue.nextTick(() => {
      const location = name
        ? { name, params: { 0: path } }
        : { path };

      $router.replace(location);
    });
  };

  $router.goBack = () => {
    if (!$storage.routerPreviousName) {
      $router.replace({ name: RouteName.HOME });
    } else {
      $router.back();
    }
  };

  $router.error = (e) => {
    e?.response?.status === 404
      ? $router.reload(RouteName.NOT_FOUND)
      : $router.reload(RouteName.ERROR);
  };

  $router.beforeEach(async (to, from, next) => {
    $EventBus.emit('unbind');

    if (to.fullPath.endsWith('.html')) {
      return next({
        path: to.fullPath.replace('.html', ''),
      });
    }

    if (DEEPLINK_ACTION_REGEX.test(to.path)) {
      return transformDeeplinkToAction({ path: to.fullPath, EventBus: $EventBus });
    }

    if (DEEPLINK_REGEX.test(to.path)) {
      const route = await transformDeeplinkToRoute({ path: to.fullPath, liveManager });
      return next(route);
    }

    const toPath = to.path.split('#')[0];
    const fromPath = from.path && from.path.split('#')[0];

    const navigate = (location = to) => {
      if (to.name !== location.name) {
        return next(location);
      }

      if (fromPath !== toPath
        || to.query !== from.query
        || to.path === '/') {
        return next();
      }
    };

    if (fromPath !== toPath
      || to.query !== from.query) {
      $spinner.show();
    }

    if (from.name) {
      $storage.routerPreviousName = from.name;
    }

    if (to.meta.navigateDirectly) {
      return navigate();
    }

    if (subscriber.isLogged && subscriber.status.suspended) {
      return navigate({ name: RouteName.SUBSCRIBER_SUSPENDED });
    }

    if (subscriber.isLogged && !subscriber.hasConsents) {
      return navigate({ name: RouteName.SUBSCRIBER_AGREEMENTS });
    }

    if (!subscriber.isLogged && to.meta.requireAuth) {
      return navigate({ name: RouteName.SUBSCRIBER_LOGIN, query: { redirect: to.fullPath } });
    }

    if ((subscriber.isLogged && to.meta.anonymousOnly)
      || (!subscriber.isAmb && to.meta.ambOnly)
      || (subscriber.isKidsTheme && to.meta.adultsOnly)) {
      return navigate(to.query.redirect || { name: RouteName.HOME });
    }

    if (to.meta.anonymousOnly && $storage.isKidsMode) {
      $storage.isKidsMode = false;
    }

    navigate();
  });

  $router.afterEach((to) => {
    $EventBus.removeAllListeners('unbind');
    updateLink(to.path.split('#')[0]);

    const metaRobots = document.querySelector('#metaRobots');
    metaRobots.content = to.meta?.noindex ? 'noindex, nofollow' : 'index, follow';

    const prerenderStatusCode = document.querySelector('#prerenderStatusCode');
    if (prerenderStatusCode) {
      prerenderStatusCode.content = to.name === RouteName.NOT_FOUND ? '410' : '200';
    }
  });
};
